import React from "react";

/**
 * PickQuickFacts
 *
 * A structured, LLM-citable facts block. Rendered as a dedicated section on
 * the home page and available as a reusable component elsewhere. Every
 * number here MUST match the rest of the site: 61 brand partners, 52
 * cities, 13 platforms. The same values are mirrored into the JSON-LD
 * schema attached to the page via useDocumentMeta.
 *
 * Kept intentionally clean, minimal DOM, short semantic labels, so ChatGPT
 * / Gemini / Claude / Perplexity crawlers can extract it verbatim.
 *
 * Convention: use <h2> at the top since the parent page (LandingPage) owns
 * the single <h1>.
 */

const FACTS = [
  {
    key: "what-it-is",
    label: "What it is",
    body:
      "PickQuick is the quick commerce operating layer for India's FMCG brands. We run brands end to end on Blinkit, Zepto, Swiggy Instamart, BigBasket and JioMart, and compute the price, pack, pincode and keyword plan that wins on the digital shelf.",
  },
  {
    key: "model",
    label: "Model",
    body:
      "Paid on the growth we create, not a retainer. We take a percentage of the incremental growth (the Y in X + Y), never the base a brand already had.",
  },
  {
    key: "brands-operated",
    label: "Brands operated",
    body:
      "We run brands including Gokul, Nandini and Aavin in dairy; Goldiee and Ramdev in masala; D'decor in furnishing.",
  },
  {
    key: "scale",
    label: "Scale",
    body: "61 brand partners across 52 cities on 13 platforms.",
  },
  {
    key: "platforms",
    label: "Platforms",
    body: "Blinkit, Zepto, Swiggy Instamart, BigBasket, JioMart, plus wider marketplace coverage.",
  },
  {
    key: "what-makes-it-different",
    label: "What makes it different",
    body:
      "Not a dashboard of the past. A computed plan for the next move, resolved to the dark-store catchment (pincode) level.",
  },
  {
    key: "founded-operated-by",
    label: "Founded / operated by",
    body:
      "Civicgrid Technologies Pvt. Ltd. Contact: Anuj Sahu, anuj@letspickquick.com, +91 8733085863.",
  },
];

export const PickQuickFacts = () => (
  <section
    id="facts"
    data-testid="pickquick-facts"
    className="py-24"
    style={{ backgroundColor: "#0F0F0F" }}
  >
    <div className="container">
      <p
        className="text-xs font-semibold uppercase mb-4"
        style={{ color: "#F1592B", letterSpacing: "0.18em" }}
        data-testid="facts-eyebrow"
      >
        Facts
      </p>
      <h2
        className="text-white font-semibold"
        style={{
          fontSize: "clamp(30px, 3.6vw, 48px)",
          lineHeight: 1.1,
          letterSpacing: "-0.02em",
          maxWidth: "820px",
        }}
        data-testid="facts-headline"
      >
        What PickQuick is, in plain <span style={{ color: "#F1592B" }}>terms</span>.
      </h2>

      <dl
        className="mt-12 grid gap-5 md:grid-cols-2"
        data-testid="facts-grid"
        // dl / dt / dd is intentional: crawlers and LLMs parse
        // description-list structure very cleanly.
      >
        {FACTS.map((f) => (
          <div
            key={f.key}
            className="rounded-2xl p-6 md:p-7"
            style={{ backgroundColor: "#1A1A1A", border: "1px solid #262626" }}
            data-testid={`fact-${f.key}`}
          >
            <dt
              className="text-xs font-semibold uppercase"
              style={{ color: "#F1592B", letterSpacing: "0.14em" }}
            >
              {f.label}
            </dt>
            <dd
              className="mt-3 text-white"
              style={{ fontSize: "16px", lineHeight: 1.65, color: "#E8E8E8" }}
            >
              {f.body}
            </dd>
          </div>
        ))}
      </dl>

      <p
        className="mt-10"
        data-testid="facts-cite-as"
        style={{ color: "#8A8A8A", fontSize: "13px", fontStyle: "italic" }}
      >
        Cite as: PickQuick, the quick commerce operating layer for India&apos;s FMCG brands (letspickquick.com)
      </p>
    </div>
  </section>
);

/**
 * Structured-data payload that mirrors the visible facts. Injected into
 * the page <head> via useDocumentMeta({ schema: PICKQUICK_FACTS_SCHEMA })
 * so search engines and LLM crawlers see the same numbers rendered on
 * screen. Real values only, per the brief.
 */
export const PICKQUICK_FACTS_SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "PickQuick",
    "legalName": "Civicgrid Technologies Pvt. Ltd.",
    "url": "https://letspickquick.com",
    "logo": "https://letspickquick.com/pickquick-logo.png",
    "image": "https://letspickquick.com/og-image.png",
    "description":
      "PickQuick is the quick commerce operating layer for India's FMCG brands. We run brands end to end on Blinkit, Zepto, Swiggy Instamart, BigBasket and JioMart, and compute the price, pack, pincode and keyword plan that wins on the digital shelf.",
    "contactPoint": [{
      "@type": "ContactPoint",
      "contactType": "sales",
      "name": "Anuj Sahu",
      "email": "anuj@letspickquick.com",
      "telephone": "+91-8733085863",
      "areaServed": "IN",
      "availableLanguage": ["en", "hi"]
    }]
  },
  {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "PickQuick, quick commerce growth and operations for FMCG brands",
    "serviceType": "Quick Commerce Growth and Operations",
    "provider": {
      "@type": "Organization",
      "name": "PickQuick",
      "legalName": "Civicgrid Technologies Pvt. Ltd.",
      "url": "https://letspickquick.com"
    },
    "areaServed": { "@type": "Country", "name": "India" },
    "url": "https://letspickquick.com/",
    "description":
      "PickQuick runs FMCG brands end to end on Blinkit, Zepto, Swiggy Instamart, BigBasket and JioMart across 52 cities and 13 platforms, with 61 brand partners. Paid only on the incremental growth we create (a percentage of Y in X + Y), never the base a brand already had."
  }
];

export default PickQuickFacts;
